import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { Toolbar } from '@/shared/components/custom/toolbar';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import { useConfirm } from '@/shared/hooks/useConfirm';
import { useSeniorityMatrix } from '../../hooks/useSeniorityMatrix';
import { useUpdateSeniorityMatrix } from '../../hooks/useUpdateSeniorityMatrix';
import { useTimeOffTypes } from '../../hooks/useTimeOffTypes';
import { seniorityMatrixSchema, type SeniorityMatrixEntryFormValues } from '../../schemas';
import { SeniorityTierFormDialog } from './SeniorityTierFormDialog';

export function SeniorityMatrixEditor() {
  const { t } = useTranslation('timeOff');
  const { data, isLoading, isError } = useSeniorityMatrix();
  const { data: types } = useTimeOffTypes();
  const updateMatrix = useUpdateSeniorityMatrix();
  const { confirm, isOpen, options, handleConfirm, handleCancel: closeConfirm } = useConfirm();

  const [entries, setEntries] = useState<SeniorityMatrixEntryFormValues[]>([]);
  const [formError, setFormError] = useState<string | null>(null);
  const [tierDialogOpen, setTierDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  // Tracks the last `data` reference synced into local state — resets `entries`
  // during render (not in an effect) when a fresh query response comes in.
  const [syncedData, setSyncedData] = useState(data);

  if (data && data !== syncedData) {
    setSyncedData(data);
    setEntries(data.map((entry) => ({ ...entry })));
    setFormError(null);
  }

  const appliesTo = (types ?? []).filter((type) => type.consumesVacationBalance);

  function openAddTier() {
    setEditingIndex(null);
    setTierDialogOpen(true);
  }

  function openEditTier(index: number) {
    setEditingIndex(index);
    setTierDialogOpen(true);
  }

  function handleTierSave(values: SeniorityMatrixEntryFormValues) {
    setEntries((prev) =>
      editingIndex === null
        ? [...prev, values]
        : prev.map((entry, i) => (i === editingIndex ? values : entry)),
    );
  }

  async function handleRemoveTier(index: number) {
    const result = await confirm({
      title: t('settings.matrix.removeTier'),
      variant: 'destructive',
    });
    if (!result.confirmed) return;
    setEntries((prev) => prev.filter((_, i) => i !== index));
  }

  function handleCancel() {
    if (data) setEntries(data.map((entry) => ({ ...entry })));
    setFormError(null);
  }

  function handleSave() {
    const result = seniorityMatrixSchema(t).safeParse({ entries });
    if (!result.success) {
      setFormError(result.error.issues[0]?.message ?? t('apiErrors.matrix.default'));
      return;
    }
    setFormError(null);
    updateMatrix.mutate(result.data);
  }

  if (isLoading) {
    return (
      <div className="space-y-3" data-testid="time-off-settings-matrix-loading-skeleton">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="time-off-settings-matrix-error">
        {t('apiErrors.matrix.default')}
      </p>
    );
  }

  return (
    <div className="space-y-4" data-testid="time-off-settings-matrix-editor">
      <Toolbar className="items-start">
        <Toolbar.Info
          title={t('settings.matrix.title')}
          description={t('settings.matrix.description')}
        />
        <Toolbar.Actions>
          <Button onClick={openAddTier} data-testid="time-off-matrix-add-tier">
            <Plus className="size-4" />
            {t('settings.matrix.addTier')}
          </Button>
        </Toolbar.Actions>
      </Toolbar>

      {appliesTo.length > 0 && (
        <div className="-mt-2 flex items-center gap-2 text-sm text-muted-foreground">
          <span>{t('settings.matrix.appliesTo')}</span>
          {appliesTo.map((type) => (
            <Badge
              key={type.id}
              variant="outline"
              className="border-transparent bg-primary/10 text-primary"
            >
              {type.name}
            </Badge>
          ))}
        </div>
      )}

      <div className="overflow-hidden rounded-xl border">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[28rem] text-sm">
            <thead className="bg-muted">
              <tr>
                <th className="px-5 py-3 text-left text-xs font-medium tracking-wide text-muted-foreground uppercase">
                  {t('settings.matrix.fromYear')}
                </th>
                <th className="px-5 py-3 text-left text-xs font-medium tracking-wide text-muted-foreground uppercase">
                  {t('settings.matrix.toYear')}
                </th>
                <th className="px-5 py-3 text-left text-xs font-medium tracking-wide text-muted-foreground uppercase">
                  {t('settings.matrix.days')}
                </th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {entries.map((entry, index) => (
                <tr key={index} data-testid={`time-off-matrix-row-${index}`}>
                  <td className="px-5 py-3">{entry.fromYear}</td>
                  <td className="px-5 py-3">
                    {entry.toYear === null ? t('settings.matrix.openEnded') : entry.toYear}
                  </td>
                  <td className="px-5 py-3">{entry.days}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => openEditTier(index)}
                            aria-label={t('settings.matrix.edit')}
                            data-testid={`time-off-matrix-edit-${index}`}
                          >
                            <Pencil className="size-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>{t('settings.matrix.edit')}</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => handleRemoveTier(index)}
                            disabled={entries.length === 1}
                            aria-label={t('settings.matrix.removeTier')}
                            className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                            data-testid={`time-off-matrix-remove-${index}`}
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>{t('settings.matrix.removeTier')}</TooltipContent>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {formError && (
        <p className="text-sm text-destructive" data-testid="time-off-matrix-error">
          {formError}
        </p>
      )}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={handleCancel}>
          {t('settings.matrix.cancel')}
        </Button>
        <Button
          type="button"
          onClick={handleSave}
          disabled={updateMatrix.isPending}
          data-testid="time-off-matrix-save"
        >
          {t('settings.matrix.save')}
        </Button>
      </div>

      <SeniorityTierFormDialog
        open={tierDialogOpen}
        onOpenChange={setTierDialogOpen}
        editingValues={editingIndex === null ? null : entries[editingIndex]}
        onSave={handleTierSave}
      />

      {isOpen && options && (
        <ConfirmDialog
          open={isOpen}
          onOpenChange={(open) => !open && closeConfirm()}
          title={options.title}
          description={options.description}
          confirmLabel={options.confirmLabel}
          cancelLabel={options.cancelLabel}
          variant={options.variant}
          onConfirm={handleConfirm}
        />
      )}
    </div>
  );
}
